/**
 * RouterFallbacks — Surface Layer (/01)
 *
 * Router-level pending + error fallbacks so NO route ever renders a blank/white screen during an async
 * `beforeLoad` (Auth Flow v2 guard) or a router-level error. Both render in the login visual language
 * (AuthWizardLayout, g100) — contained, Carbon-only, zero inline styles. Fixes REQ-001.83 (/step-up blank).
 */

import { Button, InlineNotification, SkeletonText, Stack } from '@carbon/react';
import { AuthWizardLayout } from '../components/auth/AuthWizardLayout';

/** Shown while an async route guard/loader is pending (instead of a blank screen). */
export function RouterPending() {
  return (
    <AuthWizardLayout
      heading="One moment"
      subtitle="Securing your session — this only takes a moment."
      labelId="router-pending-heading"
    >
      <SkeletonText paragraph lineCount={3} />
    </AuthWizardLayout>
  );
}

interface RouterErrorProps {
  error?: { message?: string };
}

/** Shown when a route guard/loader throws (instead of a blank screen). Offers a safe recovery path. */
export function RouterError({ error }: RouterErrorProps) {
  const message = error?.message ?? 'An unexpected error interrupted loading.';
  function returnToSignIn() {
    if (typeof window !== 'undefined') window.location.assign('/sign-in');
  }
  return (
    <AuthWizardLayout
      heading="Sign-in was interrupted"
      subtitle="We could not finish loading this page. Return to sign-in and try again."
      labelId="router-error-heading"
    >
      <Stack gap={5}>
        <InlineNotification kind="error" lowContrast hideCloseButton title="Page error" subtitle={message} />
        <Button onClick={returnToSignIn}>Return to sign-in</Button>
      </Stack>
    </AuthWizardLayout>
  );
}
